import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SimpleTooltip } from "@/components/ui/simple-tooltip";
import { cn } from "@/lib/utils";
import { DataService } from "@/services/data";
import { FavoriteCreateType, FavoriteType } from "@/types/user/favorite";
import { getObjectIdForPath, getReferenceTypeForPath } from "@/utils/content-types";
import * as LucideIcons from "lucide-react";
import { ChevronDown, LucideProps, Search, Star, StarOff } from "lucide-react";
import React, { ForwardRefExoticComponent, RefAttributes, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

export function FavoriteDropdown() {
    const [open, setOpen] = useState(false);
    const [favorites, setFavorites] = useState<FavoriteType[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    // Charger les favoris au montage du composant
    useEffect(() => {
        loadFavorites();
    }, []);

    // Recharger les favoris quand le dropdown s'ouvre (pour avoir les dernières données)
    useEffect(() => {
        if (open) {
            loadFavorites();
        }
    }, [open]);

    const loadFavorites = async () => {
        setLoading(true);
        try {
            const response = await DataService.client.get("/users/favorites/").json<{ results: FavoriteType[] }>();
            setFavorites(Array.isArray(response.results) ? response.results : []);
        } catch (error) {
            console.error("Error loading favorites:", error);
            toast.error("Could not load favorites");
            setFavorites([]);
        } finally {
            setLoading(false);
        }
    };

    // Filtrer les favoris selon la recherche
    const filteredFavorites = useMemo(() => {
        if (!Array.isArray(favorites)) return [];
        if (!searchQuery) return favorites;
        return favorites.filter(fav => fav.name.toLowerCase().includes(searchQuery.toLowerCase()));
    }, [favorites, searchQuery]);

    // Ajouter la page courante aux favoris
    const handleAddCurrentPage = async () => {
        try {
            // Vérifier si la limite n'est pas atteinte
            if (Array.isArray(favorites) && favorites.length >= 20) {
                toast.error("You have reached the limit of 20 favorites");
                return;
            }

            // Générer un nom basé sur l'URL actuelle
            const currentPath = location.pathname + location.search;
            const currentUrl = window.location.origin + currentPath;

            // Vérification supplémentaire pour éviter les doublons
            if (Array.isArray(favorites) && favorites.some(fav => fav.url === currentUrl)) {
                toast.warning("This page is already in your favorites");
                return;
            }

            const pageName = generatePageName(currentPath);
            // Utilise le slug du modèle comme content_type
            const content_type = getReferenceTypeForPath(currentPath);
            const object_id = getObjectIdForPath(currentPath);
            const newFavorite: FavoriteCreateType = {
                url: currentUrl,
                name: pageName,
                content_type: content_type ?? undefined,
                object_id: object_id ?? undefined
            };

            const response = await DataService.client.post(newFavorite, "/users/favorites/").json<FavoriteType>();

            setFavorites(prev => [response, ...prev]); // Ajouter en première position
            toast.success("Page added to favorites");
        } catch (error: any) {
            if (error.json?.non_field_errors) {
                toast.error(error.json.non_field_errors[0]);
            } else if (error.status === 400 && error.json?.url) {
                // Gestion spécifique pour l'erreur de duplication
                toast.warning("This page is already in your favorites");
                // Recharger les favoris pour synchroniser l'état
                loadFavorites();
            } else {
                toast.error("Could not add this page to favorites");
            }
            console.error("Error adding favorite:", error);
        }
    };

    // Supprimer un favori
    const handleDeleteFavorite = async (favoriteId: number, e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        try {
            await DataService.client.delete(`/users/favorites/${favoriteId}/`);
            setFavorites(prev => prev.filter(fav => fav.id !== favoriteId));
            toast.success("Favorite removed");
        } catch (error) {
            toast.error("Could not remove this favorite");
            console.error("Error removing favorite:", error);
        }
    };

    // Naviguer vers un favori
    const handleFavoriteClick = (favorite: FavoriteType) => {
        // Si c'est une URL complète de notre domaine, extraire le chemin et naviguer avec React Router
        if (favorite.url.startsWith(window.location.origin)) {
            const path = favorite.url.replace(window.location.origin, "");
            navigate(path);
        } else {
            // Pour les URLs externes, utiliser window.location.href
            window.location.href = favorite.url;
        }
        setOpen(false);
    };

    // Générer un nom de page basé sur le titre ou l'URL
    const generatePageName = (url: string): string => {
        // 1ere intention : utiliser le titre de la page en nettoyant "| Walter CRM"
        if (document.title) {
            const cleanTitle = document.title.replace(/\s*\|\s*Walter CRM\s*$/, "").trim();
            if (cleanTitle && cleanTitle !== "Walter CRM") {
                return cleanTitle;
            }
        }

        // Fallback : analyse de l'URL si le titre n'est pas informatif
        const segments = url.split("/").filter(Boolean);
        if (segments.length === 0) return "Home";

        // Essayer d'inférer un nom intelligent basé sur l'URL
        const lastSegment = segments[segments.length - 1];
        if (lastSegment === "detail" && segments.length > 1) {
            return `Detail ${segments[segments.length - 2]}`;
        }

        return lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1);
    };

    // Vérifier si la page courante est déjà dans les favoris
    const isCurrentPageFavorited = useMemo(() => {
        const currentUrl = window.location.origin + location.pathname + location.search;
        return Array.isArray(favorites) && favorites.some(fav => fav.url === currentUrl);
    }, [favorites, location]);

    // Helper pour récupérer le metadata d'un favori
    function getFavoriteMetadata(fav: FavoriteType): { icon: ForwardRefExoticComponent<Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>>; color: string } {
        if (!fav.content_type) return { icon: LucideIcons.File, color: "#888" };
        // Si content_type est un slug, cherche dans le cache metadata le bon modèle
        for (const key in localStorage) {
            if (key.startsWith("options-metadata:")) {
                const cached = localStorage.getItem(key);
                if (cached) {
                    try {
                        const { data } = JSON.parse(cached);

                        if (fav.content_type === data.content_type_id) {
                            const iconName = data?.model_metadata?.icon || "Star";
                            const color = data?.model_metadata?.color || "#888";
                            const iconRaw = LucideIcons[iconName as keyof typeof LucideIcons];
                            // Use type assertion to handle the icon component
                            const icon = (iconRaw as any) || LucideIcons.Star;
                            return { icon, color };
                        }
                    } catch {
                        continue;
                    }
                }
            }
        }
        return { icon: LucideIcons.File, color: "#888" };
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <div className="flex h-8 w-12 items-center justify-center rounded-full hover:bg-gray-100">
                    <SimpleTooltip message="Add to favorites" delayDuration={300} skipDelayDuration={300}>
                        <div
                            className={cn("flex h-9 items-center justify-center rounded-full ")}
                            onClick={e => {
                                if (!isCurrentPageFavorited) {
                                    e.stopPropagation();
                                    handleAddCurrentPage();
                                } else {
                                    setOpen(!open);
                                }
                            }}
                        >
                            <Star size={20} strokeWidth={1.5} className={isCurrentPageFavorited ? "fill-yellow-500 text-yellow-500" : "text-gray-700"} />
                        </div>
                    </SimpleTooltip>

                    <SimpleTooltip message="See my favorites" delayDuration={300} skipDelayDuration={300}>
                        <div className="flex h-8 items-center justify-center rounded-full  text-slate-500" onClick={() => setOpen(!open)}>
                            <ChevronDown size={14} className="text-gray-700" />
                        </div>
                    </SimpleTooltip>
                </div>
            </PopoverTrigger>

            <PopoverContent className="mr-10 w-96 p-0" align="start">
                <div className="flex flex-col">
                    {/* Header avec compteur */}
                    <div className="flex items-center justify-between border-b px-4 py-3">
                        <h3 className="text-sm font-semibold">My favorites</h3>
                        <span className="text-xs text-gray-500">{Array.isArray(favorites) ? favorites.length : 0}/20</span>
                    </div>

                    {/* Barre de recherche */}
                    <div className="relative p-2">
                        <Search className="absolute left-5 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
                        <Input placeholder="Search a favorite..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="h-9 pl-10" />
                    </div>

                    {/* Liste des favoris */}
                    <ScrollArea className="h-72 w-full">
                        <div className="w-full p-2">
                            {loading ? (
                                <div className="flex items-center justify-center py-6">
                                    <div className="text-sm text-gray-500">Loading...</div>
                                </div>
                            ) : filteredFavorites.length > 0 ? (
                                filteredFavorites.map(favorite => {
                                    const { icon: Icon, color } = getFavoriteMetadata(favorite);
                                    return (
                                        <div key={favorite.id} className="group relative mb-1 w-full cursor-pointer rounded-md hover:bg-gray-100">
                                            <div className="flex w-full items-center justify-between px-2 py-2" onClick={() => handleFavoriteClick(favorite)}>
                                                <div className="flex max-w-80 items-center  gap-2 overflow-hidden truncate pr-2 text-sm font-medium">
                                                    <div
                                                        className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded"
                                                        style={{
                                                            backgroundColor: color
                                                        }}
                                                    >
                                                        <Icon size={16} className="text-white" />
                                                    </div>
                                                    <div className="truncate">{favorite.name}</div>
                                                </div>
                                                <div
                                                    className="flex h-6 w-6 flex-shrink-0 items-center justify-center text-gray-400 opacity-0 hover:text-red-600 group-hover:opacity-100"
                                                    onClick={e => handleDeleteFavorite(favorite.id, e)}
                                                >
                                                    <StarOff size={16} />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="py-6 text-center text-gray-500">
                                    <p className="text-sm">{searchQuery ? "No favorite found" : "No favorite registered"}</p>
                                    <p className="text-sm">{searchQuery ? "Try another search term" : "Add pages to your favorites to access them quickly"}</p>
                                </div>
                            )}
                        </div>
                    </ScrollArea>
                </div>
            </PopoverContent>
        </Popover>
    );
}
